import React, { useEffect, useState } from 'react';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import {
    trackConversionEvent,
    hasNudgedThisSession,
    markNudgeShown,
} from '../utils/telemetry';
import { getExperimentVariant } from '../services/api';

/**
 * UpgradeNudge — Subtle, contextual, frequency-capped upgrade surface.
 *
 * Phase 3A: Shows a small inline card at high-value moments to
 * help Free users discover what paid plans offer.
 * Phase 4A: Supports experiment variants for copy A/B testing.
 *
 * Rules:
 * - Only shown to Free users
 * - Frequency-capped: once per session per nudge type
 * - Non-blocking: can be dismissed
 * - Contextual: copy varies by nudgeType and experiment variant
 * - Tracks impression and click events with variant metadata
 *
 * Props:
 *   nudgeType   – 'post_lesson' | 'locked_preset' | 'audio_ready'
 *   planTier    – current user's plan tier
 *   onUpgrade   – function to open PricingPage (receives source string)
 *   featureArea – optional context for analytics (e.g. 'lesson', 'audio')
 *   compact     – if true, renders a single-line version
 */

// Copy variants keyed by nudgeType → variant letter
const NUDGE_COPY = {
    post_lesson: {
        A: {
            title: 'Enhanced Quality Available',
            body: 'Pro adds deeper instructional reasoning, formative check-ins, and richer lesson structure.',
            cta: 'See Pro Plans',
        },
        B: {
            title: 'Your Lessons, Even Stronger',
            body: 'Upgrade for multi-pass refinement, smarter differentiation, and TEKS-aligned check-ins.',
            cta: 'Explore Pro',
        },
    },
    locked_preset: {
        A: {
            title: 'More Voice Styles',
            body: 'Unlock Warm Narration, Engaging Explanation, and Calm Directions with a paid plan.',
            cta: 'See Plans',
        },
        B: {
            title: 'Unlock All Voice Presets',
            body: 'Sound exactly how your students need — warm, calm, or engaging. Upgrade to access all styles.',
            cta: 'Unlock Voices',
        },
    },
    audio_ready: {
        A: {
            title: 'Premium Audio Quality',
            body: 'Your voice is set up! Pro and Premium unlock richer narration scripts and additional voice styles.',
            cta: 'Explore Plans',
        },
        B: {
            title: 'Your Voice Is Ready — Go Premium',
            body: 'Get longer scripts, more presets, and higher-quality narration with a paid plan.',
            cta: 'See Premium',
        },
    },
};

// Map nudgeType to variant config key
const VARIANT_KEYS = {
    post_lesson: 'nudge_post_lesson_variant',
    locked_preset: 'nudge_locked_preset_variant',
    audio_ready: 'nudge_audio_ready_variant',
};

const UpgradeNudge = ({
    nudgeType = 'post_lesson',
    planTier = 'Free',
    onUpgrade,
    featureArea,
    compact = false,
}) => {
    // Resolve experiment variant
    const variantKey = VARIANT_KEYS[nudgeType] || VARIANT_KEYS.post_lesson;
    const variant = getExperimentVariant(variantKey, 'A');

    // Calculate visibility once on mount — no setState needed
    const shouldShow = () => {
        const tier = (planTier || 'Free').toLowerCase();
        if (tier !== 'free') return false;
        if (hasNudgedThisSession(nudgeType)) return false;
        try {
            const flags = JSON.parse(localStorage.getItem('saige_feature_flags') || '{}');
            if (flags.upgrade_nudges === false) return false;
        } catch { /* show by default */ }
        return true;
    };

    const [visible] = useState(shouldShow);
    const [dismissed, setDismissed] = useState(false);

    // Track impression + mark shown (one-time side effect, no state mutation)
    useEffect(() => {
        if (!visible) return;
        markNudgeShown(nudgeType);
        trackConversionEvent('upgrade_nudge_shown', {
            nudge_type: nudgeType,
            source: `nudge_${nudgeType}`,
            feature_area: featureArea,
            experiment_name: `nudge_${nudgeType}`,
            variant,
        });
    }, [visible, nudgeType, featureArea, variant]);

    if (!visible || dismissed) return null;

    const copySet = NUDGE_COPY[nudgeType] || NUDGE_COPY.post_lesson;
    const copy = copySet[variant] || copySet.A;
    const source = `nudge_${nudgeType}`;

    const handleClick = () => {
        trackConversionEvent('upgrade_nudge_clicked', {
            nudge_type: nudgeType,
            source,
            feature_area: featureArea,
            experiment_name: `nudge_${nudgeType}`,
            variant,
        });
        if (onUpgrade) onUpgrade(source);
    };

    const handleDismiss = (e) => {
        e.stopPropagation();
        setDismissed(true);
    };

    if (compact) {
        return (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                <Sparkles size={12} className="text-amber-500 flex-shrink-0" />
                <span className="flex-1">{copy.body}</span>
                <button
                    onClick={handleClick}
                    className="font-medium text-amber-600 hover:text-amber-800 whitespace-nowrap flex items-center gap-1"
                >
                    {copy.cta} <ArrowRight size={12} />
                </button>
                <button onClick={handleDismiss} className="text-amber-400 hover:text-amber-600 ml-1">
                    <X size={12} />
                </button>
            </div>
        );
    }

    return (
        <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl relative">
            <button
                onClick={handleDismiss}
                className="absolute top-2 right-2 p-1 text-amber-400 hover:text-amber-600 rounded-md transition-colors"
            >
                <X size={14} />
            </button>
            <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles size={16} className="text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-900">{copy.title}</p>
                    <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">{copy.body}</p>
                    <button
                        onClick={handleClick}
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 transition-colors"
                    >
                        {copy.cta} <ArrowRight size={12} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpgradeNudge;
